import { useState, useEffect, useCallback } from "react"
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Switch,
  Alert,
  Snackbar,
  Alert as MuiAlert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material"
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material"
import { RichTextEditor } from "./DraftEditor"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import { fetchToken } from "../../../../Auth"
import { adminURL } from "../../../../global"
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import { useForm, Controller, FormProvider } from "react-hook-form"

// Optimized SubsectionDialog component to prevent lag
function SubsectionDialog({ open, itemId, onClose, onAdd }) {
  // Use local state for the input value to avoid re-renders in the parent component
  const [heading, setHeading] = useState("")

  // Reset input value when dialog opens
  useEffect(() => {
    if (open) {
      setHeading("")
    }
  }, [open])

  // Handle input change locally without updating parent state
  const handleInputChange = (e) => {
    setHeading(e.target.value)
  }

  // Only when the user submits, we pass the final value to the parent
  const handleSubmit = () => {
    // Make sure heading is not empty and itemId is valid
    if (heading.trim() && itemId) {
      onAdd(itemId, heading)
      console.log("Adding subsection:", { itemId, heading })
    } else {
      console.error("Cannot add subsection: Invalid itemId or empty heading", { itemId, heading })
    }
  }

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="subsection-dialog-title">
      <DialogTitle id="subsection-dialog-title">Add Subsection</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Subsection Heading"
          fullWidth
          value={heading}
          onChange={handleInputChange}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={!heading}>
          Add
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default function CareerSection({ privileges }) {
  const { createPrivilege, deletePrivilege, editPrivilege, livePrivilege } = privileges

  // Add navigate for redirection on auth errors
  const navigate = useNavigate()
  const [totalApplications, setTotalApplications] = useState({
    total_count: 0,
    new_count: 0,
  })
  const [individualApplications, setIndividualApplications] = useState({})
  const [itemSubsectionMap, setItemSubsectionMap] = useState({})
  // Core state
  const [careerItems, setCareerItems] = useState([])
  const [maxId, setMaxId] = useState(0)
  const [isEditMode, setIsEditMode] = useState(false)
  const [unsavedSubsections, setUnsavedSubsections] = useState({})
  // Mode tracking (view, create, edit)
  const [mode, setMode] = useState("view")

  // Track items marked for deletion
  const [pendingDeletions, setPendingDeletions] = useState([])

  // Track newly created item - changed from array to single value
  const [newItemId, setNewItemId] = useState(null)

  // Store original state before editing
  const [originalCareerItems, setOriginalCareerItems] = useState([])

  // UI state
  const [expandedItems, setExpandedItems] = useState({})
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    itemId: null,
  })

  // OPTIMIZED: Changed subsectionDialog state to only track open state and itemId
  const [subsectionDialog, setSubsectionDialog] = useState({
    open: false,
    itemId: null,
  })

  // Add dialog for live status confirmation
  const [dialogOpen, setDialogOpen] = useState(false)
  const [pendingChange, setPendingChange] = useState(null)

  // Validation state
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false)
  const [errors, setErrors] = useState({})
  const [subsectionErrors, setSubsectionErrors] = useState({})
  const [generalErrors, setGeneralErrors] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  // Content state
  const [realTimeContent, setRealTimeContent] = useState({})

  // Track which editors have unsaved changes for alerts
  const [editorsWithUnsavedChanges, setEditorsWithUnsavedChanges] = useState({})
  const [deleteSubsectionDialog, setDeleteSubsectionDialog] = useState({
    open: false,
    itemId: null,
    subsectionId: null,
  })
  // Add toast message state
  const [toastMessage, setToastMessage] = useState({
    open: false,
    message: "",
    severity: "info",
  })

  // Add a new state for tracking pending subsection deletions
  const [pendingSubsectionDeletions, setPendingSubsectionDeletions] = useState([])

  // Add a new state to track whether the test URL should be shown for each item:
  const [showTestUrl, setShowTestUrl] = useState({})

  // Add a new state to store the original showTestUrl state
  const [originalShowTestUrl, setOriginalShowTestUrl] = useState({})

  // Add a state to force re-render of rich text editors when canceling
  const [editorResetCounter, setEditorResetCounter] = useState(0)

  // Create a form methods object for React Hook Form
  const formMethods = useForm()
  const { control, handleSubmit, reset, setValue, getValues, formState } = formMethods

  // Add this near other useState declarations
  const [isLoading, setIsLoading] = useState(false)

  // When entering edit mode, store the original showTestUrl state
  useEffect(() => {
    if (isEditMode) {
      // Create a proper deep copy of careerItems to ensure we don't have reference issues
      // const deepCopy = JSON.parse(JSON.stringify(careerItems))
      setOriginalCareerItems(careerItems)
      setOriginalShowTestUrl({ ...showTestUrl })
      console.log("Stored original items:", careerItems)

      // Initialize form values when entering edit mode
      const formValues = {}
      careerItems.forEach((item) => {
        formValues[`title-${item.id}`] = item.title
        formValues[`location-${item.id}`] = item.location || ""
        formValues[`experience-${item.id}`] = item.experience || ""
        formValues[`introduction-${item.id}`] = item.introduction || ""
        formValues[`learningUrl-${item.id}`] = item.learningUrl || ""
        formValues[`testUrl-${item.id}`] = item.testUrl || ""
      })
      reset(formValues)
    } else {
      setNewItemId(null)
      setPendingDeletions([])
      setPendingSubsectionDeletions([])
    }
  }, [isEditMode])

  // Add function to fetch career items
  const getCareerItems = async () => {
    setIsLoading(true) // Set loading to true when starting
    try {
      const response = await axios.get(`/backend/get_career_item/0`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })
      setCareerItems(response.data || [])

      if (response.data && response.data.length > 0) {
        const initialShowTestUrl = {}
        response.data.forEach((item) => {
          initialShowTestUrl[item.id] = Boolean(item.testUrl)
        })
        setShowTestUrl(initialShowTestUrl)
      }

      console.log("Career items loaded:", response.data)
      if (response.data && response.data.length > 0) {
        const newItemSubsectionMap = {}
        const globalMaxSubsectionId = 0

        response.data.forEach((item) => {
          newItemSubsectionMap[item.id] = item.maxSubsectionId
        })

        setItemSubsectionMap(newItemSubsectionMap)
      }

      // Return the response data so we can use it in the promise chain
      return response.data
    } catch (err) {
      console.error("Error fetching career items:", err)
      if (err.response?.status === 401) {
        navigate(`/niyantran-panel/${adminURL}/login`)
      }
      setToastMessage({
        open: true,
        message: "Failed to load career items",
        severity: "error",
      })
      return []
    } finally {
      setIsLoading(false) // Set loading to false when done, whether success or error
    }
  }

  // Add function to fetch max IDs
  const fetchMaxId = async () => {
    try {
      const response = await axios.get("/backend/get_career_max_id", {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      if (response.status === 200) {
        setMaxId(response.data.nextId)
      } else {
        console.error("Failed to get max IDs:", response.data.msg)
      }
    } catch (error) {
      console.error("Error fetching max IDs:", error)
    }
  }

  // Add a new state to store the URL options from the API
  const [learningUrlOptions, setLearningUrlOptions] = useState([])

  // Add a function to fetch the URL options
  const fetchLearningUrlOptions = async () => {
    try {
      const response = await axios.get("/backend/get_career_urls", {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      if (response.status === 200) {
        setLearningUrlOptions(response.data || [])
      } else {
        console.error("Failed to get learning URL options:", response.data.msg)
      }
    } catch (error) {
      console.error("Error fetching learning URL options:", error)
    }
  }

  // Initialize data on component mount
  useEffect(() => {
    getCareerItems()
    fetchMaxId()
    fetchTotalApplications()
    fetchLearningUrlOptions() // Add this line to fetch URL options
    // fetchIndivitualApplications()
    // fetchTotalNews()
    // fetchIndivitualApplications()
    // fetchIndivitualNew()
  }, [navigate])

  // Helper function to get current content (real-time or stored)
  const getCurrentContent = useCallback(
    (itemId, subsectionId, storedContent) => {
      const realTimeSubContent = realTimeContent[`${itemId}-${subsectionId}`]
      return realTimeSubContent !== undefined ? realTimeSubContent : storedContent || ""
    },
    [realTimeContent],
  )

  // Function to check if an item is editable based on the current mode
  const isItemEditable = (itemId) => {
    if (mode === "create") {
      // Only the newly created item is editable in create mode
      return itemId === newItemId
    }
    // All items are editable in edit mode
    return mode === "edit"
  }

  // Function to check if sequence controls should be enabled
  const isSequenceEnabled = () => {
    // Sequence controls are disabled in create mode
    return mode === "edit"
  }

  // Helper function to check if an item is pending deletion
  const isItemPendingDeletion = (itemId) => {
    return pendingDeletions.includes(itemId)
  }

  // Check if a career item is complete with all required fields
  const isItemComplete = (item) => {
    const formValues = getValues()
    return Boolean(
      formValues[`title-${item.id}`] &&
        formValues[`introduction-${item.id}`] &&
        formValues[`learningUrl-${item.id}`] &&
        item.testUrl &&
        item.subsections.length > 0 &&
        item.subsections.every((sub) => sub.heading && (sub.content || realTimeContent[`${item.id}-${sub.id}`])),
    )
  }

  const clearUnsavedSubsectionsForItem = (itemId) => {
    setUnsavedSubsections((prev) => {
      const newState = { ...prev }
      // Remove all entries that start with this item's ID
      Object.keys(newState).forEach((key) => {
        if (key.startsWith(`${itemId}-`)) {
          delete newState[key]
        }
      })
      return newState
    })
  }

  // Add a new career item
  const handleAddCareerItem = () => {
    if (!createPrivilege) return

    // Set mode to 'create' when adding a new item
    setMode("create")
    setIsEditMode(true)

    const newId = maxId + 1
    const newItem = {
      id: newId,
      title: "",
      location: "",
      experience: "",
      introduction: "",
      subsections: [],
      learningUrl: "",
      testUrl: "",
      isLive: false,
      sequence: -1,
    }
    setNewItemId(newId)
    // Initialize the subsection map for this new item
    setItemSubsectionMap((prev) => ({
      ...prev,
      [newId]: 0,
    }))
    setCareerItems([newItem, ...careerItems])
    setExpandedItems({
      [newId]: true,
    })
    setShowTestUrl((prev) => ({
      ...prev,
      [newId]: false,
    }))

    // Initialize form values for the new item
    setValue(`title-${newId}`, "")
    setValue(`location-${newId}`, "")
    setValue(`experience-${newId}`, "")
    setValue(`introduction-${newId}`, "")
    setValue(`learningUrl-${newId}`, "")
    setValue(`testUrl-${newId}`, "")
  }

  // Update career item field - now using React Hook Form
  const handleCareerItemChange = (id, field, value) => {
    // This function is now primarily used for non-form fields or programmatic updates
    setCareerItems((prevItems) => {
      const itemIndex = prevItems.findIndex((item) => item.id === id)
      if (itemIndex === -1) return prevItems

      const newItems = [...prevItems]
      newItems[itemIndex] = {
        ...newItems[itemIndex],
        [field]: value,
      }

      return newItems
    })

    // Clear error for this field if validation was attempted
    if (hasAttemptedSave) {
      setErrors((prev) => ({
        ...prev,
        [id]: { ...(prev[id] || {}), [field]: undefined },
      }))
    }
  }

  // Handle live status change directly from accordion summary
  const handleLiveStatusChange = async (itemId, isLive, e) => {
    e.stopPropagation()

    try {
      const response = await axios.post(
        "/backend/change_career_live",
        {
          id: itemId,
          flag: isLive ? 1 : 0,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
          },
        },
      )

      if (response.status === 200) {
        setToastMessage({
          open: true,
          message: "Career item live status updated successfully!",
          severity: "success",
        })
        resetState()
      }
    } catch (error) {
      console.error("Error updating career live status:", error)
      setToastMessage({
        open: true,
        message: error.response?.data?.msg || "Failed to update career live status",
        severity: "error",
      })
    }
  }

  // Mark item for deletion instead of deleting immediately
  const handleMarkItemForDeletion = (itemId) => {
    if (itemId === newItemId) {
      setCareerItems(careerItems.filter((item) => item.id !== itemId))

      // Clean up related state
      setExpandedItems((prev) => {
        const newState = { ...prev }
        delete newState[itemId]
        return newState
      })

      setErrors((prev) => {
        const newState = { ...prev }
        delete newState[itemId]
        return newState
      })

      // Clean up real-time content for this item
      setRealTimeContent((prev) => {
        const newContent = { ...prev }
        Object.keys(newContent).forEach((key) => {
          if (key.startsWith(`${itemId}-`)) {
            delete newContent[key]
          }
        })
        return newContent
      })

      // Clean up editors with unsaved changes
      setEditorsWithUnsavedChanges((prev) => {
        const newState = { ...prev }
        Object.keys(newState).forEach((key) => {
          if (key.startsWith(`${itemId}-`)) {
            delete newState[key]
          }
        })
        return newState
      })
      console.log("reached here")
      setItemSubsectionMap((prev) => {
        const newMap = { ...prev }
        delete newMap[itemId]
        return newMap
      })
    } else {
      // Add existing items to the pending deletions list
      setPendingDeletions((prev) => [...prev, itemId])
      clearUnsavedSubsectionsForItem(itemId)
    }

    setDeleteDialog({ open: false, itemId: null })
  }

  // Add this function to handle API deletion - now only called during save
  const handleDeleteCareerItems = async (itemIds) => {
    try {
      // Prepare the payload as required by the backend
      const payload = {
        ids: itemIds,
      }

      // Call the delete API
      const response = await axios.post("/backend/delete_career_item", payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })

      // Return the result so the save function can track successful deletions
      return response.data
    } catch (error) {
      console.error(`Error deleting career items:`, error)
      throw new Error(error.response?.data?.msg || `Failed to delete career items`)
    }
  }

  // Delete a career item
  const handleDeleteConfirm = () => {
    handleMarkItemForDeletion(deleteDialog.itemId)
  }

  // Move an item up or down in the list
  const handleMoveItem = (index, direction) => {
    // Only allow sequence changes in edit mode
    if (!isSequenceEnabled()) return

    const newItems = [...careerItems]
    const newIndex = index + direction

    if (newIndex >= 0 && newIndex < newItems.length) {
      // Swap items
      ;[newItems[index], newItems[newIndex]] = [newItems[newIndex], newItems[index]]

      // Update sequences for live items
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        sequence: index + 1,
      }))

      setCareerItems(updatedItems)
    }
  }

  // OPTIMIZED: Modified to accept heading as a parameter instead of reading from state
  const handleAddSubsection = (itemId, heading) => {
    console.log("handleAddSubsection called with:", { itemId, heading })

    // Convert itemId to number if it's a string
    const numericItemId = typeof itemId === "string" ? Number.parseInt(itemId, 10) : itemId

    const currentMaxForItem = itemSubsectionMap[numericItemId] || 0
    const newSubsectionId = currentMaxForItem + 1

    console.log("Adding subsection with ID:", newSubsectionId, "to item:", numericItemId)

    setCareerItems((items) => {
      const updatedItems = items.map((item) => {
        if (item.id === numericItemId) {
          console.log("Found matching item:", item.id)
          return {
            ...item,
            subsections: [
              ...item.subsections,
              {
                id: newSubsectionId,
                heading,
                content: "",
              },
            ],
          }
        }
        return item
      })

      console.log("Updated items:", updatedItems)
      return updatedItems
    })

    setItemSubsectionMap((prev) => {
      const updated = {
        ...prev,
        [numericItemId]: newSubsectionId,
      }
      console.log("Updated subsection map:", updated)
      return updated
    })

    setSubsectionDialog({ open: false, itemId: null })

    // Clear any subsection-related errors for this item
    setGeneralErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[numericItemId]
      return newErrors
    })
  }

  const removeTrailingBreaks = (content) => {
    if (!content) return ""

    // This regex matches any number of <br>, <br/>, or <br /> tags at the end of the string
    return content.replace(/(<br\s*\/?>\s*)+$/g, "")
  }

  // Handle subsection change
  const handleSubsectionChange = (itemId, subsectionId, content) => {
    // Remove trailing <br/> tags
    const cleanedContent = removeTrailingBreaks(content)

    // Validate content before saving
    if (!cleanedContent || cleanedContent.trim() === "") {
      setSubsectionErrors((prev) => ({
        ...prev,
        [`${itemId}-${subsectionId}`]: "Subsection content cannot be empty",
      }))

      setToastMessage({
        open: true,
        message: "Subsection content cannot be empty",
        severity: "error",
      })
      return
    }

    // Update both stored and real-time content with cleaned content
    setCareerItems((items) =>
      items.map((item) => {
        if (item.id === itemId) {
          return {
            ...item,
            subsections: item.subsections.map((sub) =>
              sub.id === subsectionId ? { ...sub, content: cleanedContent } : sub,
            ),
          }
        }
        return item
      }),
    )

    setRealTimeContent((prev) => ({
      ...prev,
      [`${itemId}-${subsectionId}`]: cleanedContent,
    }))
    // Mark this specific editor as no longer having unsaved changes
    setEditorsWithUnsavedChanges((prev) => {
      const newState = { ...prev }
      delete newState[`${itemId}-${subsectionId}`]
      return newState
    })

    setUnsavedSubsections((prev) => {
      const newState = { ...prev }
      delete newState[`${itemId}-${subsectionId}`]
      return newState
    })
  }

  // Custom function to handle accordion expansion/collapse
  const handleAccordionChange = (itemId) => {
    // In create mode, if trying to collapse the new item, check if it's complete
    if (mode === "create" && itemId === newItemId && expandedItems[itemId]) {
      const item = careerItems.find((item) => item.id === itemId)

      // If the item is not complete, prevent collapsing
      if (item && !isItemComplete(item)) {
        setToastMessage({
          open: true,
          message: "Please complete all required fields before collapsing this item.",
          severity: "warning",
        })
        return
      }
    }

    // Toggle the expanded state
    toggleExpand(itemId)
  }

  // Toggle accordion expansion
  const toggleExpand = (id) => {
    setExpandedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  // Validate all fields before saving
  const validateFields = () => {
    const newErrors = {}
    let isValid = true
    const errorDetails = []
    const formValues = getValues()

    // In create mode, only validate the new item
    const itemsToValidate =
      mode === "create"
        ? careerItems.filter((item) => item.id === newItemId)
        : careerItems.filter((item) => !pendingDeletions.includes(item.id))

    itemsToValidate.forEach((item) => {
      const itemErrors = {}
      const missingFields = []

      // Required text fields
      if (!formValues[`title-${item.id}`]?.trim()) {
        itemErrors.title = "Title is required"
        missingFields.push("Title")
        isValid = false
      }
      if (!formValues[`introduction-${item.id}`]?.trim()) {
        itemErrors.introduction = "Introduction is required"
        missingFields.push("Introduction")
        isValid = false
      }

      // Check for empty subsection content
      if (item.subsections.length > 0) {
        const emptySubsections = item.subsections.filter((sub) => {
          const realTimeSubContent = realTimeContent[`${item.id}-${sub.id}`]
          return (
            (!sub.content || sub.content.trim() === "") && (!realTimeSubContent || realTimeSubContent.trim() === "")
          )
        })

        if (emptySubsections.length > 0) {
          itemErrors.subsections = `${emptySubsections.length} subsection(s) have empty content`
          missingFields.push(`${emptySubsections.length} subsection content`)
          isValid = false
        }
      } else {
        itemErrors.subsections = "At least one subsection is required"
        missingFields.push("Subsections")
        isValid = false
      }

      if (Object.keys(itemErrors).length > 0) {
        newErrors[item.id] = itemErrors
        errorDetails.push({
          id: item.id,
          title: formValues[`title-${item.id}`] || "Untitled Job Item",
          missingFields,
        })
      }
    })

    setErrors(newErrors)
    return { isValid, errorDetails }
  }

  // Reset state after operations
  const resetState = () => {
    setMode("view")
    setIsEditMode(false)
    setHasAttemptedSave(false)
    setSubsectionErrors({})
    setExpandedItems({})
    setNewItemId(null)
    setPendingDeletions([])
    setPendingSubsectionDeletions([])
    setCareerItems([])
    setOriginalCareerItems([])
    setEditorsWithUnsavedChanges({})
    setRealTimeContent({})

    // First reset the form to clear all values
    reset({})
    // Then fetch new data
    getCareerItems()
    fetchMaxId()
    fetchTotalApplications()
  }

  // Save all changes - now using React Hook Form
  const handleSave = async () => {
    setHasAttemptedSave(true)
    setGeneralErrors({})
    setIsSaving(true)

    const hasUnsavedSubsections = Object.values(unsavedSubsections).some((value) => value === true)
    if (hasUnsavedSubsections) {
      console.log("reach")
      // Get the list of subsections that still exist and have unsaved changes
      const existingUnsavedSubsections = Object.keys(unsavedSubsections).filter((key) => {
        const [itemId, subsectionId] = key.split("-")
        return careerItems.some(
          (item) => item.id.toString() === itemId && item.subsections.some((sub) => sub.id.toString() === subsectionId),
        )
      })

      if (existingUnsavedSubsections.length > 0) {
        setToastMessage({
          open: true,
          message: "Please save all editor content before proceeding",
          severity: "warning",
        })
        setIsSaving(false)
        return
      }
    }
    // Check if there are unsaved changes in any rich text editor
    if (Object.keys(editorsWithUnsavedChanges).length > 0) {
      const editorKeys = Object.keys(editorsWithUnsavedChanges)
      let editorInfo = ""

      if (editorKeys.length <= 3) {
        editorInfo = editorKeys
          .map((key) => {
            const [itemId, subsectionId] = key.split("-")
            const item = careerItems.find((i) => i.id.toString() === itemId)
            if (item) {
              const subsection = item.subsections.find((s) => s.id.toString() === subsectionId)
              if (subsection) {
                return `"${item.title || "Untitled"} > ${subsection.heading || "Untitled"}"`
              }
            }
            return key
          })
          .join(", ")

        setToastMessage({
          open: true,
          message: `You have unsaved changes in the following editor(s): ${editorInfo}. Please save your content in each editor before proceeding.`,
          severity: "warning",
        })
      } else {
        setToastMessage({
          open: true,
          message: `You have unsaved changes in ${editorKeys.length} editors. Please save your content in each editor before proceeding.`,
          severity: "warning",
        })
      }

      setIsSaving(false)
      return
    }

    // Check if each item has at least one subsection
    let missingSubsections = false

    // In create mode, only validate the new item
    const itemsToValidate =
      mode === "create"
        ? careerItems.filter((item) => item.id === newItemId)
        : careerItems.filter((item) => !pendingDeletions.includes(item.id))

    itemsToValidate.forEach((item) => {
      // Count subsections that aren't pending deletion
      const remainingSubsections = item.subsections.filter(
        (sub) => !pendingSubsectionDeletions.includes(`${item.id}-${sub.id}`),
      )

      if (remainingSubsections.length === 0) {
        setGeneralErrors((prev) => ({
          ...prev,
          [item.id]: "At least one subsection is required. Please click 'Add Subsection*' to add content.",
        }))
        missingSubsections = true
      }
    })

    if (missingSubsections) {
      setToastMessage({
        open: true,
        message: "One or more job items are missing subsections. Please add at least one subsection to each item.",
        severity: "error",
      })
      setIsSaving(false)
      return
    }

    // Check for empty subsections
    let hasEmptySubsections = false
    itemsToValidate.forEach((item) => {
      item.subsections.forEach((sub) => {
        const content = sub.content

        // Check if content is empty or only contains <br/> tags
        const isOnlyBreakTags = content && content.replace(/<br\s*\/?>/gi, "").trim() === ""
        const isOnlyEmptyPTags =
          content &&
          (content.replace(/<p><\/p>/gi, "").trim() === "" || content.replace(/<p>\s*<\/p>/gi, "").trim() === "")

        if (!content || content.trim() === "" || isOnlyBreakTags || isOnlyEmptyPTags) {
          hasEmptySubsections = true
        }
      })
    })
    if (hasEmptySubsections) {
      setToastMessage({
        open: true,
        message: "One or more subsections have empty content. Please fill in all subsections before saving.",
        severity: "error",
      })
      setIsSaving(false)
      return
    }

    const result = validateFields()
    if (result.isValid) {
      // Get form values
      const formValues = getValues()

      // Process pending deletions first
      if (pendingDeletions.length > 0) {
        console.log("Processing pending deletions:", pendingDeletions)
        try {
          await handleDeleteCareerItems(pendingDeletions)
          console.log("Successfully deleted items:", pendingDeletions)
        } catch (error) {
          console.error("Failed to delete items:", error)
          throw new Error(`Failed to delete items: ${error.message}`)
        }
      }

      // Process pending subsection deletions
      let itemsToProcess = [...careerItems]

      if (pendingSubsectionDeletions.length > 0) {
        console.log("Processing pending subsection deletions:", pendingSubsectionDeletions)

        // Apply subsection deletions to the items
        itemsToProcess = careerItems.map((item) => {
          const itemSubsectionsToDelete = pendingSubsectionDeletions
            .filter((key) => key.startsWith(`${item.id}-`))
            .map((key) => Number.parseInt(key.split("-")[1]))

          if (itemSubsectionsToDelete.length > 0) {
            return {
              ...item,
              subsections: item.subsections.filter((sub) => !itemSubsectionsToDelete.includes(sub.id)),
            }
          }
          return item
        })
      }

      // Apply all real-time content to the actual items before saving
      // Now using itemsToProcess which has the subsection deletions applied
      const updatedItems = itemsToProcess.map((item) => {
        // Get form values for this item
        const formData = {
          ...item,
          title: formValues[`title-${item.id}`] || item.title,
          location: formValues[`location-${item.id}`] || item.location,
          experience: formValues[`experience-${item.id}`] || item.experience,
          introduction: formValues[`introduction-${item.id}`] || item.introduction,
          learningUrl: formValues[`learningUrl-${item.id}`] || item.learningUrl,
          subsections: item.subsections.map((sub) => {
            const realTimeSubContent = realTimeContent[`${item.id}-${sub.id}`]
            return {
              ...sub,
              content: realTimeSubContent !== undefined ? realTimeSubContent : sub.content,
            }
          }),
        }
        return formData
      })

      // Filter out testUrl from payload when hidden
      const processedItems = updatedItems.map((item) => {
        if (!showTestUrl[item.id]) {
          const { testUrl, ...itemWithoutTestUrl } = item
          return itemWithoutTestUrl
        }
        return item
      })

      try {
        // Then create or update the career items based on mode
        if (mode === "create") {
          // Only create the new item
          const item = processedItems.find((item) => item.id === newItemId)
          if (item) {
            const itemToSend = !showTestUrl[item.id] ? (({ testUrl, ...rest }) => rest)(item) : item
            const payload = {
              ...itemToSend,
              maxSubsectionId: itemSubsectionMap[item.id] || 0,
            }
            try {
              const response = await axios.post("/backend/create_career_item", payload, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                },
              })
              console.log("Career item created successfully:", response.data)
            } catch (error) {
              console.error("Error creating career item:", error)
              throw new Error(error.response?.data?.msg || "Failed to create career item")
            }
          }
        } else if (mode === "edit") {
          // Filter out pending deletions when handling updates
          const itemsToUpdate = processedItems
            .filter((item) => item.id !== newItemId)
            .filter((item) => !pendingDeletions.includes(item.id))

          // Prepare an array of edited items to send in a single API call
          const editedItems = []

          // Process each item to check if it's been edited
          for (const item of itemsToUpdate) {
            // Find the original item to compare changes
            const originalItem = originalCareerItems.find((orig) => orig.id === item.id)

            // Check if any field has been edited
            let isEdited = false
            let onlySequenceChanged = false

            if (originalItem) {
              // Compare each field individually
              const titleChanged = item.title !== originalItem.title
              const introChanged = item.introduction !== originalItem.introduction
              const locationChanged = item.location !== originalItem.location
              const experienceChanged = item.experience !== originalItem.experience
              const learningUrlChanged = item.learningUrl !== originalItem.learningUrl
              const testUrlChanged = item.testUrl !== originalItem.testUrl
              const sequenceChanged = item.sequence !== originalItem.sequence

         
              let subsectionsChanged = false

              // Check if subsection count has changed
              if (item.subsections.length !== originalItem.subsections.length) {
                subsectionsChanged = true
                // console.log(
                //   `Item ${item.id} subsection count changed: ${originalItem.subsections.length} -> ${item.subsections.length}`,
                // )
              } else {
                // Check each subsection for changes
                for (let i = 0; i < item.subsections.length; i++) {
                  const currentSub = item.subsections[i]
                  const originalSub = originalItem.subsections[i]

                  if (
                    currentSub.id !== originalSub.id ||
                    currentSub.heading !== originalSub.heading ||
                    currentSub.content !== originalSub.content
                  ) {
                    subsectionsChanged = true
                    // console.log(`Item ${item.id} subsection ${currentSub.id} changed`)
                    break
                  }
                }
              }
                onlySequenceChanged = sequenceChanged && 
                  !titleChanged && 
                  !introChanged && 
                  !locationChanged && 
                  !experienceChanged && 
                  !learningUrlChanged && 
                  !testUrlChanged && 
                  !subsectionsChanged;

              isEdited =
                titleChanged ||
                introChanged ||
                locationChanged ||
                experienceChanged ||
                learningUrlChanged ||
                testUrlChanged ||
                sequenceChanged ||
                subsectionsChanged

              // Add debug logging
              if (isEdited) {
                console.log(`Item ${item.id} has been edited. Changes:`, {
                  titleChanged,
                  introChanged,
                  locationChanged,
                  experienceChanged,
                  learningUrlChanged,
                  testUrlChanged,
                  sequenceChanged,
                  subsectionsChanged,
                  onlySequenceChanged,
                })
              }
            }

            // Skip items that haven't been edited
            if (!isEdited) {
              console.log(`Skipping item ${item.id} as it hasn't been edited`)
              continue
            }

            // Add to the array of edited items
            editedItems.push({
              ...item,
              isSequenceChanged: onlySequenceChanged,
            })
          }

          // If there are edited items, send them in a batch
          if (editedItems.length > 0) {
            try {
              console.log(`Sending batch update for ${editedItems.length} edited items`)

              const editPayload = editedItems.map((item) => {
                const itemToSend = !showTestUrl[item.id] ? (({ testUrl, ...rest }) => rest)(item) : item
                console.log(itemToSend)
                return {
                  ...itemToSend,
                  maxSubsectionId: itemSubsectionMap[item.id] || 0,
                }
              })
              // Call the edit_career_items API with an array of items
              const response = await axios.post("/backend/edit_career_item", editPayload, {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
                },
              })

              console.log(`All career items updated successfully:`, response.data)
            } catch (error) {
              console.error("Error updating career items:", error)
              throw new Error(error.response?.data?.msg || `Failed to update career items`)
            }
          } else {
            console.log("No items were edited, skipping API call")
          }
        }

        setToastMessage({
          open: true,
          message: "Career items saved successfully!",
          severity: "success",
        })

        // Reset state after successful save
        resetState()
      } catch (error) {
        console.error("Error saving career items:", error)
        setToastMessage({
          open: true,
          message: `Failed to save career items: ${error.message}`,
          severity: "error",
        })
      } finally {
        setIsSaving(false)
      }
    } else {
      const errorMessages = result.errorDetails.map(
        (item) => `Career item "${item.title}"  is missing: ${item.missingFields.join(", ")}`,
      )

      setToastMessage({
        open: true,
        message:
          errorMessages.length > 1
            ? `Multiple items have validation errors: ${errorMessages.join(" | ")}`
            : errorMessages[0],
        severity: "error",
      })
      setIsSaving(false)
    }
  }

  // Enter edit mode
  const handleEdit = () => {
    if (editPrivilege) {
      setMode("edit")
      setIsEditMode(true)
      setHasAttemptedSave(false)
      setErrors({})
      setOriginalCareerItems(careerItems)
      setPendingDeletions([])
    }
  }

  // Handle cancel editing
  const handleCancel = () => {
    // If in create mode with new item, show confirmation dialog
    if (mode === "create" && newItemId !== null) {
      setShowCancelDialog(true)
    } else {
      // For other cases, just show the dialog
      setShowCancelDialog(true)
    }
  }

  const handleCancelConfirm = () => {
    // If we have a new item that hasn't been saved, remove it
    if (newItemId !== null) {
      setCareerItems(careerItems.filter((item) => item.id !== newItemId))
    } else {
      // If no new item, revert to the original state - using the deep copy we stored
      // Make sure we're using the exact original items with their subsections
      // setCareerItems(JSON.parse(JSON.stringify(originalCareerItems)))
      setCareerItems(originalCareerItems)

    }

    // Restore the original showTestUrl state
    setShowTestUrl(originalShowTestUrl)

    // Clear all unsaved changes tracking
    setUnsavedSubsections({})
    setEditorsWithUnsavedChanges({})

    // Reset realTimeContent to clear any saved editor content
    setRealTimeContent({})

    // Increment the editor reset counter to force re-render of all editors
    setEditorResetCounter((prev) => prev + 1)

    // Reset state
    setMode("view")
    setIsEditMode(false)
    setHasAttemptedSave(false)
    setErrors({})
    setShowCancelDialog(false)
    setNewItemId(null)
    setPendingDeletions([])
    setPendingSubsectionDeletions([])

    // Reset form to original values
    reset({})
  }

  const handleCancelDialogClose = () => {
    setShowCancelDialog(false)
  }

  // Handle switch toggle without triggering accordion expand/collapse
  const handleSwitchToggle = (e, itemId, checked) => {
    e.stopPropagation()
    // For direct toggle in view mode
    if (!isEditMode) {
      setPendingChange({
        itemId: itemId,
        isLive: checked,
        event: e,
      })
      setDialogOpen(true)
    } else {
      // For toggle in edit mode
      handleCareerItemChange(itemId, "isLive", checked)
    }
  }
  const handleDeleteSubsectionClick = (itemId, subsectionId, e) => {
    e.stopPropagation() // Prevent event bubbling
    setDeleteSubsectionDialog({
      open: true,
      itemId,
      subsectionId,
    })
  }
  const handleCloseDeleteSubsectionDialog = () => {
    setDeleteSubsectionDialog({
      open: false,
      itemId: null,
      subsectionId: null,
    })
  }
  const fetchTotalApplications = async () => {
    try {
      const response = await axios.get("/backend/get_appli_counts", {
        headers: {
          Authorization: `Bearer ${fetchToken("nuprice_access_token")}`,
        },
      })
      // console.log("RAJA")

      if (response.status === 200) {
        setIndividualApplications(response.data)
        setTotalApplications((prevState) => ({
          ...prevState,
          total_count: response.data.all.total,
          new_count: response.data.all.new,
        }))
      } else {
        console.error("Failed to get application count:", response.data.msg)
      }
    } catch (error) {
      console.error("Error fetching application count:", error)
    }
  }

  const handleDeleteSubsectionConfirm = () => {
    const { itemId, subsectionId } = deleteSubsectionDialog

    // Find the current item
    const item = careerItems.find((item) => item.id === itemId)

    // Check if this is the last subsection or if all other subsections are already pending deletion
    const remainingSubsections = item?.subsections.filter(
      (sub) => sub.id !== subsectionId && !pendingSubsectionDeletions.includes(`${itemId}-${sub.id}`),
    )

    // If this would be the last subsection, show an error and prevent deletion
    if (remainingSubsections?.length === 0) {
      setToastMessage({
        open: true,
        message: "Cannot delete all subsections. At least one subsection is required.",
        severity: "error",
      })

      setDeleteSubsectionDialog({
        open: false,
        itemId: null,
        subsectionId: null,
      })

      return
    }

    // For newly created subsections, remove them immediately
    const isNewItem = itemId === newItemId

    if (isNewItem || item?.subsections.find((sub) => sub.id === subsectionId)?.content === "") {
      // If it's a new item or empty subsection, remove it immediately
      setCareerItems((items) =>
        items.map((item) => {
          if (item.id === itemId) {
            return {
              ...item,
              subsections: item.subsections.filter((subsection) => subsection.id !== subsectionId),
            }
          }
          return item
        }),
      )
    } else {
      // Otherwise, mark it for deletion
      setPendingSubsectionDeletions((prev) => [...prev, `${itemId}-${subsectionId}`])
    }

    // Clean up related state
    setUnsavedSubsections((prev) => {
      const newState = { ...prev }
      delete newState[`${itemId}-${subsectionId}`]
      return newState
    })

    // Remove from editors with unsaved changes
    setEditorsWithUnsavedChanges((prev) => {
      const newState = { ...prev }
      delete newState[`${itemId}-${subsectionId}`]
      return newState
    })

    // Remove any errors for this subsection
    setSubsectionErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[`${itemId}-${subsectionId}`]
      return newErrors
    })

    // Close the dialog
    setDeleteSubsectionDialog({
      open: false,
      itemId: null,
      subsectionId: null,
    })
  }

  // Add a helper function to check if a subsection is pending deletion
  const isSubsectionPendingDeletion = (itemId, subsectionId) => {
    // Use exact matching to prevent partial matches
    return pendingSubsectionDeletions.some((key) => key === `${itemId}-${subsectionId}`)
  }

  // OPTIMIZED: Simplified to just close the dialog
  const handleSubsectionDialogCancel = () => {
    setSubsectionDialog({ open: false, itemId: null })
  }

  // Add this useEffect after your other useEffects
  useEffect(() => {
    if (!isEditMode && careerItems.length > 0) {
      careerItems.forEach((item) => {
        setValue(`title-${item.id}`, item.title || "")
        setValue(`location-${item.id}`, item.location || "")
        setValue(`experience-${item.id}`, item.experience || "")
        setValue(`introduction-${item.id}`, item.introduction || "")
        setValue(`learningUrl-${item.id}`, item.learningUrl || "")
        setValue(`testUrl-${item.id}`, item.testUrl || "")
      })
    }
  }, [careerItems, isEditMode, setValue])

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ width: "100%", maxWidth: "100%" }}>
        {/* Header section */}
        <Box sx={{ display: "flex", flexDirection: "column", mb: 4 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            <Box>
              <Typography variant="h4" gutterBottom>
                Career
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Manage career paths and learning resources
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 2 }}>
              {isEditMode ? (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSubmit(handleSave)}
                    disabled={isSaving}
                  >
                    {isSaving ? "Saving..." : mode === "create" ? "Save Job" : "Save Changes"}
                  </Button>
                  <Button variant="outlined" color="warning" onClick={handleCancel}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {editPrivilege && (
                    <Button variant="outlined" startIcon={<EditIcon />} onClick={handleEdit}>
                      Edit
                    </Button>
                  )}
                </>
              )}

              {/* Add Job button is conditionally visible based on mode */}
              {createPrivilege && mode !== "edit" && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddCareerItem}
                  disabled={mode === "create"}
                >
                  Add Job Item
                </Button>
              )}
            </Box>
          </Box>

          {/* Mode indicator */}
          {isEditMode && (
            <Box sx={{ mb: 3 }}>
              <Alert
                severity="info"
                icon={mode === "create" ? <AddIcon /> : <EditIcon />}
                action={
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.5,
                      borderRadius: "4px",
                      backgroundColor: mode === "create" ? "rgba(25, 118, 210, 0.12)" : "rgba(156, 39, 176, 0.12)",
                      color: mode === "create" ? "primary.main" : "secondary.main",
                      border: "1px solid",
                      borderColor: mode === "create" ? "primary.light" : "secondary.light",
                      typography: "caption",
                      fontWeight: "medium",
                    }}
                  >
                    {mode === "create" ? "Create Mode" : "Edit Mode"}
                    <Box
                      component="span"
                      sx={{
                        display: "inline-block",
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        ml: 1,
                        backgroundColor: mode === "create" ? "primary.main" : "secondary.main",
                      }}
                    />
                  </Box>
                }
              >
                {mode === "create"
                  ? "You are creating a new job item. Other items cannot be edited at this time."
                  : "You are in edit mode. You can modify all job items."}
              </Alert>
            </Box>
          )}

          {/* Show warning if there are unsaved editor changes */}
          {Object.keys(editorsWithUnsavedChanges).length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="warning">
                You have unsaved changes in {Object.keys(editorsWithUnsavedChanges).length} editor(s). Please save your
                content in each editor before saving the entire form.
              </Alert>
            </Box>
          )}

          {/* Show a summary of pending deletions if any */}
          {pendingDeletions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="warning">
                {pendingDeletions.length} {pendingDeletions.length === 1 ? "item" : "items"} marked for deletion.
                Changes will be applied when you click "Save Changes".
              </Alert>
            </Box>
          )}
          {pendingSubsectionDeletions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Alert severity="warning">
                {pendingSubsectionDeletions.length}{" "}
                {pendingSubsectionDeletions.length === 1 ? "subsection" : "subsections"} marked for deletion. Changes
                will be applied when you click "Save Changes".
              </Alert>
            </Box>
          )}

          {/* Added stat boxes */}
          <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
            <Box
              sx={{
                flex: 1,
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                boxShadow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                minHeight: 120,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Active Jobs
              </Typography>
              <Typography variant="h4" color="primary">
                {careerItems.filter((item) => item.isLive && !pendingDeletions.includes(item.id)).length}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 2,
                boxShadow: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "background.paper",
                minHeight: 120,
              }}
            >
              <Typography variant="h6" gutterBottom>
                Applications
              </Typography>
              <Typography variant="h4" sx={{ color: "#26a69a" }}>
                {totalApplications.total_count}
              </Typography>
              <Typography variant="body1" sx={{ color: "#795548" }}>
                {totalApplications.new_count} New
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Career items list */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          {careerItems.map((item, index) => {
            const isPendingDeletion = isItemPendingDeletion(item.id)
            const isNewItem = item.id === newItemId
            const isGreyedOut = mode === "create" && !isNewItem

            return (
              <Accordion
                key={item.id}
                expanded={Boolean(expandedItems[item.id])}
                onChange={() => handleAccordionChange(item.id)}
                sx={{
                  backgroundColor: isPendingDeletion
                    ? "rgba(255, 0, 0, 0.05)"
                    : isGreyedOut
                      ? "rgba(0, 0, 0, 0.05)"
                      : "white",
                  boxShadow: 1,
                  borderRadius: 2,
                  mb: 2,
                  opacity: isPendingDeletion || isGreyedOut ? 0.7 : 1,
                  "&:before": {
                    display: "none",
                  },
                }}
                disabled={isGreyedOut}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    px: 1.5,
                    pb: 1.5,
                    "& .MuiAccordionSummary-content": {
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      m: 0,
                    },
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="h6">{item.title || "New Job Item"} </Typography>
                    {(item.location || item.experience) && (
                      <Typography variant="body2" color="text.secondary">
                        {item.location && `Location: ${item.location} `}
                        {item.location && item.experience && " | "}
                        {item.experience && `Experience: ${item.experience}`}
                        {item.experience && " | "}
                        {individualApplications[item.title] &&
                          `Applications: ${individualApplications[item.title].total} `}
                        {item.experience && individualApplications[item.title] && " | "}
                        {individualApplications[item.title] && `New: ${individualApplications[item.title].new}`}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    {livePrivilege && mode !== "create" && (
                      <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
                        <Typography variant="body2" sx={{ mr: 1 }}>
                          Status:
                        </Typography>
                        <Switch
                          checked={Boolean(item.isLive)}
                          onChange={(e) => handleSwitchToggle(e, item.id, e.target.checked)}
                          onClick={(e) => e.stopPropagation()}
                          disabled={!livePrivilege || isPendingDeletion || mode === "edit" || mode === "create"}
                          color="primary"
                          size="small"
                        />
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                          {item.isLive ? "Published" : "Draft"}
                        </Typography>
                      </Box>
                    )}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMoveItem(index, -1)
                      }}
                      disabled={index === 0 || !isEditMode || !isSequenceEnabled() || isPendingDeletion || isGreyedOut}
                    >
                      <ArrowUpwardIcon />
                    </IconButton>
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation()
                        handleMoveItem(index, 1)
                      }}
                      disabled={
                        index === careerItems.length - 1 ||
                        !isEditMode ||
                        !isSequenceEnabled() ||
                        isPendingDeletion ||
                        isGreyedOut
                      }
                    >
                      <ArrowDownwardIcon />
                    </IconButton>
                    {/* Hide delete icon completely in create mode for non-new items */}
                    {mode !== "create" && mode !== "view" && (
                      <IconButton
                        color={isPendingDeletion ? "default" : "error"}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (!isPendingDeletion) {
                            setDeleteDialog({ open: true, itemId: item.id })
                          }
                        }}
                        disabled={!isEditMode || !deletePrivilege || isPendingDeletion || isGreyedOut}
                        sx={{ display: deletePrivilege ? "flex" : "none" }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pb: 3 }}>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {/* Title field - now using React Hook Form */}
                    <Controller
                      name={`title-${item.id}`}
                      control={control}
                      defaultValue={item.title || ""}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label="Title"
                          disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          error={hasAttemptedSave && Boolean(errors[item.id]?.title)}
                          helperText={hasAttemptedSave && errors[item.id]?.title}
                        />
                      )}
                    />

                    {/* Location and Experience fields - now using React Hook Form */}
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Controller
                        name={`location-${item.id}`}
                        control={control}
                        defaultValue={item.location || ""}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            sx={{ flex: 1 }}
                            label="Location"
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          />
                        )}
                      />
                      <Controller
                        name={`experience-${item.id}`}
                        control={control}
                        defaultValue={item.experience || ""}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            sx={{ flex: 1 }}
                            label="Experience Level"
                            disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          />
                        )}
                      />
                    </Box>

                    {/* Introduction field - now using React Hook Form */}
                    <Controller
                      name={`introduction-${item.id}`}
                      control={control}
                      defaultValue={item.introduction || ""}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          required
                          label="Introduction"
                          multiline
                          rows={4}
                          disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                          error={hasAttemptedSave && Boolean(errors[item.id]?.introduction)}
                          helperText={hasAttemptedSave && errors[item.id]?.introduction}
                        />
                      )}
                    />

                    {/* Subsections */}
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="h6">Subsections</Typography>
                          {isEditMode && isItemEditable(item.id) && !isPendingDeletion && (
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-end",
                              }}
                            >
                              <Button
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={() =>
                                  setSubsectionDialog({
                                    open: true,
                                    itemId: item.id,
                                  })
                                }
                              >
                                Add Subsection
                              </Button>
                              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                                * Required - at least one subsection must be added
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>

                      {/* Subsection error message */}
                      {generalErrors[item.id] && (
                        <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                          {generalErrors[item.id]}
                        </Alert>
                      )}

                      {/* Subsection list */}
                      {item.subsections.map((subsection) => {
                        const isSubsectionDeleting = isSubsectionPendingDeletion(item.id, subsection.id)

                        return (
                          <Box
                            key={subsection.id}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              opacity: isSubsectionDeleting ? 0.6 : 1,
                              backgroundColor: isSubsectionDeleting ? "rgba(255, 0, 0, 0.05)" : "transparent",
                              padding: isSubsectionDeleting ? 2 : 0,
                              borderRadius: isSubsectionDeleting ? 1 : 0,
                              position: "relative",
                            }}
                          >
                            {isSubsectionDeleting && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  right: 48,
                                  backgroundColor: "error.light",
                                  color: "white",
                                  px: 1,
                                  py: 0.5,
                                  borderRadius: 1,
                                  fontSize: "0.75rem",
                                  fontWeight: "bold",
                                }}
                              >
                                Pending Deletion
                              </Box>
                            )}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <Typography variant="subtitle1" fontWeight="bold">
                                {subsection.heading}
                              </Typography>
                              {!isEditMode && !getCurrentContent(item.id, subsection.id, subsection.content) && (
                                <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                  (Empty content)
                                </Typography>
                              )}
                              {isEditMode && isItemEditable(item.id) && !isPendingDeletion && (
                                <IconButton
                                  aria-label="delete"
                                  onClick={(e) => handleDeleteSubsectionClick(item.id, subsection.id, e)}
                                  style={{ marginLeft: "10px" }}
                                  color={isSubsectionDeleting ? "default" : "error"}
                                  disabled={isSubsectionDeleting}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>

                            {/* Rich text editor */}
                            <RichTextEditor
                              key={`editor-${item.id}-${subsection.id}-${editorResetCounter}`}
                              disabled={
                                !isEditMode || !isItemEditable(item.id) || isPendingDeletion || isSubsectionDeleting
                              }
                              setUnsaved={false}
                              setUnsavedSubsections={setUnsavedSubsections}
                              item={item}
                              subsection={subsection}
                              onSave={(content) => handleSubsectionChange(item.id, subsection.id, content)}
                              initialContent={getCurrentContent(item.id, subsection.id, subsection.content)}
                              error={Boolean(subsectionErrors[`${item.id}-${subsection.id}`])}
                              parentEditMode={isEditMode}
                            />
                            {/* Subsection error message */}
                            {subsectionErrors[`${item.id}-${subsection.id}`] && (
                              <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                {subsectionErrors[`${item.id}-${subsection.id}`]}
                              </Typography>
                            )}
                          </Box>
                        )
                      })}
                    </Box>

                    {/* URL fields */}
                    <Box sx={{ display: "flex", gap: 2 }}>
                      <Box sx={{ flex: 1, maxWidth: "500px" }}>
                        <Controller
                          name={`learningUrl-${item.id}`}
                          control={control}
                          defaultValue={item.learningUrl || ""}
                          render={({ field }) => (
                            <FormControl
                              fullWidth
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                            >
                              <InputLabel id={`learning-url-label-${item.id}`}>Learning Material URL</InputLabel>
                              <Select
                                {...field}
                                labelId={`learning-url-label-${item.id}`}
                                label="Learning Material URL"
                              >
                                <MenuItem value="">Select None</MenuItem>
                                {learningUrlOptions.map((option) => (
                                  <MenuItem key={option.pageURL} value={option.pageURL}>
                                    {option.title} ({option.pageURL})
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          )}
                        />
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                          sx={{
                            flex: showTestUrl[item.id] ? 0.4 : 1,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="body2">Test Your Readiness URL</Typography>
                          <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                              {showTestUrl[item.id] ? "Show" : "Hide"}
                            </Typography>
                            <Switch
                              size="small"
                              checked={Boolean(showTestUrl[item.id])}
                              onChange={(e) => {
                                setShowTestUrl((prev) => ({
                                  ...prev,
                                  [item.id]: e.target.checked,
                                }))
                                if (e.target.checked) {
                                  handleCareerItemChange(item.id, "testUrl", "https://apti.sciative.com/practice_test/")
                                }
                              }}
                              disabled={!isEditMode || !isItemEditable(item.id) || isPendingDeletion}
                            />
                          </Box>
                        </Box>
                        {showTestUrl[item.id] && (
                          <TextField
                            sx={{ flex: 0.6, maxWidth: "400px" }}
                            label="Test Your Readiness URL"
                            value={item.testUrl || "https://apti.sciative.com/practice_test/"}
                            disabled={true}
                            InputProps={{
                              readOnly: true,
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )
          })}
        </Box>

        {/* Delete confirmation dialog */}
        <Dialog
          open={deleteDialog.open}
          onClose={() => setDeleteDialog({ open: false, itemId: null })}
          aria-labelledby="delete-dialog-title"
        >
          <DialogTitle id="delete-dialog-title">Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              {mode === "edit"
                ? "This item will be marked for deletion and will be deleted when you save changes. Proceed?"
                : "Are you sure you want to delete this job item?"}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialog({ open: false, itemId: null })}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" disabled={isSaving}>
              {mode === "edit" ? "Mark for Deletion" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Live status confirmation dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} aria-labelledby="live-status-dialog-title">
          <DialogTitle id="live-status-dialog-title">Confirm Live Status Change</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to {pendingChange?.isLive ? "publish" : "unpublish"} this job item?
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="primary">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (pendingChange) {
                  handleLiveStatusChange(pendingChange.itemId, pendingChange.isLive, pendingChange.event)
                }
                setDialogOpen(false)
                setPendingChange(null)
              }}
              color="primary"
              variant="contained"
            >
              Confirm
            </Button>
          </DialogActions>
        </Dialog>

        {/* OPTIMIZED: Using the new SubsectionDialog component */}
        <SubsectionDialog
          open={subsectionDialog.open}
          itemId={subsectionDialog.itemId}
          onClose={handleSubsectionDialogCancel}
          onAdd={handleAddSubsection}
        />

        {/* Cancel confirmation dialog */}
        <Dialog open={showCancelDialog} onClose={handleCancelDialogClose} aria-labelledby="cancel-dialog-title">
          <DialogTitle id="cancel-dialog-title">Cancel Changes</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to cancel your changes? Unsaved changes will be lost.</Typography>
            {pendingDeletions.length > 0 && (
              <Typography sx={{ mt: 1, color: "warning.main" }}>
                {pendingDeletions.length} {pendingDeletions.length === 1 ? "item" : "items"} marked for deletion will be
                restored.
              </Typography>
            )}
            {pendingSubsectionDeletions.length > 0 && (
              <Typography sx={{ mt: 1, color: "warning.main" }}>
                {pendingSubsectionDeletions.length}{" "}
                {pendingSubsectionDeletions.length === 1 ? "subsection" : "subsections"} marked for deletion will be
                restored.
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelDialogClose}>No, Keep Editing</Button>
            <Button onClick={handleCancelConfirm} color="error" variant="contained">
              Yes, Cancel Changes
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteSubsectionDialog.open} onClose={handleCloseDeleteSubsectionDialog}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography>
              {mode === "edit" && deleteSubsectionDialog.itemId !== newItemId
                ? "This subsection will be marked for deletion and will be deleted when you save changes. Proceed?"
                : "Are you sure you want to delete this subsection? This action cannot be undone."}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteSubsectionDialog}>Cancel</Button>
            <Button onClick={handleDeleteSubsectionConfirm} color="error">
              {mode === "edit" && deleteSubsectionDialog.itemId !== newItemId ? "Mark for Deletion" : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Toast Message */}
        <Snackbar
          open={toastMessage.open}
          autoHideDuration={6000}
          onClose={() => setToastMessage((prev) => ({ ...prev, open: false }))}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <MuiAlert
            elevation={6}
            variant="filled"
            severity={toastMessage.severity}
            onClose={() => setToastMessage((prev) => ({ ...prev, open: false }))}
          >
            {toastMessage.message}
          </MuiAlert>
        </Snackbar>
      </Box>
    </FormProvider>
  )
}

